import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E12 — BASTION. Le premier ennemi ordinaire à parties destructibles.
 *
 *  ── Ce qu'il enseigne ──
 *  Il est le professeur des boss. Jusqu'ici, seuls les boss se démontent, et le
 *  joueur découvre le mécanisme au pire moment : face à 20 000 PV, sous six
 *  armes, en tirant au centre sans comprendre pourquoi rien ne se passe.
 *
 *  Le Bastion enseigne la même chose pour 1 800 PV et sans risque de mort. Ses
 *  trois tourelles se détachent visiblement du fuselage, elles meurent en
 *  quelques secondes chacune, et LEUR DESTRUCTION SE RESSENT IMMÉDIATEMENT :
 *  une arme en moins, tout de suite. C'est ce retour instantané qui fait la
 *  leçon — un joueur qui a démonté trois Bastions arrive devant le Colosse en
 *  sachant déjà quoi faire, sans qu'on le lui ait jamais dit.
 *
 *  ── Le noyau n'est PAS blindé ──
 *  Contrairement aux boss, on peut le tuer sans toucher aux tourelles. C'est
 *  délibéré : le Bastion pose un CHOIX, pas une contrainte.
 *
 *    tuer le noyau      600 PV, rapide, mais les tourelles descendent avec lui
 *                       et on a subi leur feu pendant tout ce temps.
 *    tuer les tourelles 1 200 PV, plus long, mais on désarme d'abord.
 *
 *  Le joueur qui découvre le mécanisme sur un ennemi où il a le droit de se
 *  tromper l'aborde ensuite sur un boss où il ne l'a plus. C'est l'ordre
 *  d'apprentissage correct, et il est rare qu'il soit respecté.
 *
 *  ── Lent, et c'est ce qui le rend lisible ──
 *  60 u/s en descente droite : il traverse l'écran en plus de vingt secondes.
 *  Le joueur a tout le temps de tourner autour, de choisir sa tourelle, de
 *  changer d'avis. Un ennemi qui pose un choix doit laisser le temps de le
 *  faire, sinon ce n'est plus un choix mais un réflexe. */
export default defineEnemy({
  id: 'bastion',
  rank: Rank.Heavy,
  hp: 600,
  visualRadius: 46,
  score: 900,
  // 1,1 s : le télégraphe des lourds (PLAN.md §6.5). Le joueur doit avoir le
  // temps de catégoriser la silhouette avant qu'elle ne devienne dangereuse.
  telegraph: 1.1,
  drops: 4,
  motion: { kind: Motion.Linear, speed: 60, amplitude: 0, frequency: 0, holdY: 0 },
  emitter: {
    // Le noyau tire peu et large : l'essentiel de la menace est dans les
    // tourelles, sinon les détruire ne se ressentirait pas.
    pattern: Pattern.Fan,
    every: 3.2,
    count: 5,
    spreadDeg: 90,
    speed: 320,
    firstDelay: 2.4,
    family: Fam.EnemyRed,
  },

  /* ── Les trois tourelles ───────────────────────────────────────────────────
     Désynchronisées de 0,6 s (voir leurs `firstDelay` respectifs). C'est ce qui
     empêche le Bastion de produire une salve unique et massive : trois cadences
     décalées donnent une pression CONTINUE mais franchissable, là où trois
     armes synchronisées donneraient trois murs et deux silences.

     ⚠️ Les ordonnées sont positives (au-dessus du fuselage) ou faibles : le
     Bastion descend jusqu'en bas du terrain, et une tourelle placée trop bas
     finirait par tirer sous Y = -180, ce que PLAN.md §6.6 interdit sans
     télégraphe dédié. À offY 0 et +26, la tourelle la plus basse est au niveau
     du fuselage — donc elle sort du cadre en même temps que lui. */
  parts: [
    { enemy: 'bastion-tourelle-a', offX: -52, offY: 0 },
    { enemy: 'bastion-tourelle-b', offX: 52, offY: 0 },
    { enemy: 'bastion-tourelle-c', offX: 0, offY: 26 },
  ],
});
